import { z } from "zod";
import apiClient from "../../../shared/api/client";
import type {
  CreateGuestReleaseData,
  GuestPaginationParams,
  GuestRelease,
  GuestReleaseList,
  GuestStats,
  UpdateGuestReleaseData,
} from "../types";

const guestReleaseSchema = z.object({
  id: z.string(),
  version: z.string(),
  status: z.enum(["draft", "published", "archived"]),
  downloadUrl: z.string(),
  fileSize: z.coerce.number(),
  fileName: z.string(),
  releaseNotes: z.string(),
  publishedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  downloads: z.number(),
  platform: z.enum(["windows", "android", "macos"]),
  environment: z.enum(["dev", "staging", "production"]),
  updaterUrl: z.string().nullable().optional(),
  updaterFileName: z.string().nullable().optional(),
  updaterFileSize: z.coerce.number().nullable().optional(),
});

const guestReleaseListSchema = z.object({
  data: z.array(guestReleaseSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});

const guestStatsSchema = z.object({
  totalReleases: z.number(),
  publishedReleases: z.number(),
  draftReleases: z.number(),
  totalDownloads: z.number(),
  latestVersion: z.string(),
});

export const getReleases = async (
  params?: GuestPaginationParams,
): Promise<GuestReleaseList> => {
  const response = await apiClient.get("/admin/guest/releases", { params });
  const parsed = guestReleaseListSchema.parse(response.data);
  return parsed;
};

export const getRelease = async (id: string): Promise<GuestRelease> => {
  const response = await apiClient.get(`/admin/guest/releases/${id}`);
  const parsed = guestReleaseSchema.parse(response.data);
  return parsed;
};

export const createRelease = async (
  data: CreateGuestReleaseData,
  onUploadProgress?: (progress: number) => void,
): Promise<GuestRelease> => {
  const formData = new FormData();
  formData.append("version", data.version);
  formData.append("releaseNotes", data.releaseNotes);
  formData.append("platform", data.platform);
  formData.append("environment", data.environment);
  formData.append("installer", data.installer);
  if (data.updater && data.platform === "windows") {
    formData.append("updater", data.updater);
  }

  const response = await apiClient.post("/admin/guest/releases", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: (event) => {
      if (event.total && onUploadProgress) {
        const percentCompleted = Math.round((event.loaded * 100) / event.total);
        onUploadProgress(percentCompleted);
      }
    },
  });
  const parsed = guestReleaseSchema.parse(response.data);
  return parsed;
};

export const updateRelease = async (
  id: string,
  data: UpdateGuestReleaseData,
): Promise<GuestRelease> => {
  const response = await apiClient.patch(`/admin/guest/releases/${id}`, data);
  const parsed = guestReleaseSchema.parse(response.data);
  return parsed;
};

export const deleteRelease = async (id: string): Promise<void> => {
  await apiClient.delete(`/admin/guest/releases/${id}`);
};

export const publishRelease = async (id: string): Promise<GuestRelease> => {
  const response = await apiClient.post(`/admin/guest/releases/${id}/publish`);
  const parsed = guestReleaseSchema.parse(response.data);
  return parsed;
};

export const archiveRelease = async (id: string): Promise<GuestRelease> => {
  const response = await apiClient.post(`/admin/guest/releases/${id}/archive`);
  const parsed = guestReleaseSchema.parse(response.data);
  return parsed;
};

export const getStats = async (): Promise<GuestStats> => {
  const response = await apiClient.get("/admin/guest/releases/stats");
  const parsed = guestStatsSchema.parse(response.data);
  return parsed;
};

export const guestApi = {
  getReleases,
  getRelease,
  createRelease,
  updateRelease,
  deleteRelease,
  publishRelease,
  archiveRelease,
  getStats,
};
